import { flat, parseEntity, refNames } from '../entity/entity';
import type { VaultEntity } from '../entity/entity';

/**
 * The arc FACET over a vault entity: the board's vocabulary of status, hand
 * order and what an arc holds.
 *
 * Everything file-shaped lives in `entity/`. What is here is what only the
 * plan cares about — and it reads the same file any other surface would,
 * rather than a different parse.
 */

export { flat, listOf, readableBody } from '../entity/entity';
export type { VaultEntity } from '../entity/entity';

/**
 * The canonical order, and `needs-input` LEADS it — ahead of `active`.
 *
 * Everything else here is work someone can get on with; this one is stopped until the
 * reader does something. It drives the board's column order and the status picker, and
 * the 1–5 hotkeys ride the same array, so `1` now sets needs-input.
 */
export const STATUSES = ['needs-input', 'active', 'next', 'parked', 'done'] as const;
export type Status = (typeof STATUSES)[number];

/**
 * Being worked, or waiting on you — as opposed to merely queued. Mirrors `ArcStatus.inFlight` in
 * the app, deliberately: the two heads disagreeing about what counts as live is how a status ends
 * up rendered in one place and invisible in the other.
 *
 * These two travel together. A surface that shows what is in play shows both, and each item says
 * which it is through its own glyph rather than through the band it sits in.
 */
export const IN_FLIGHT: readonly Status[] = ['needs-input', 'active'];

/** Set aside rather than in play — the statuses a resting surface files away. */
export const RETIRED: readonly Status[] = ['parked', 'done'];

export function isInFlight(status: Status): boolean {
  return IN_FLIGHT.includes(status);
}

/** In flight OR queued: everything that is still open. `next` joins the two above. */
export function isLive(status: Status): boolean {
  return !RETIRED.includes(status);
}

/**
 * The palette variable a status wears — `needs-input` is the reason this is a function
 * rather than capitalising the first letter at each call site.
 */
export function statusVar(status: string): string {
  const camel = status.replace(/-(.)/g, (_, c: string) => c.toUpperCase());
  return `--status${camel.charAt(0).toUpperCase()}${camel.slice(1)}`;
}

/** An entity read through the plan's lens. `status` and `order` always have
 *  a value here — the fallbacks are the board's, not the file's, and
 *  `capabilitiesOf` is what says whether the file actually declared them. */
export interface Arc extends VaultEntity {
  status: Status;
  order: number;
  /** Filed away: off the board whatever its status still says. */
  archived: boolean;
}

export function parseArc(path: string, raw: string): Arc {
  const entity = parseEntity(path, raw);
  const declared = entity.frontMatter['status'];
  const n = Number.parseInt(String(entity.frontMatter['order'] ?? ''), 10);
  return {
    ...entity,
    // An unrecognised status must not drop the arc off the board.
    status: STATUSES.includes(declared as Status) ? (declared as Status) : 'next',
    // Never hand-ordered → sorts last.
    order: Number.isFinite(n) ? n : 1e9,
    archived: flat(entity.frontMatter['visibility']) === 'archived',
  };
}

/**
 * Filed away — superseded and out of sight, without being deleted and without
 * claiming to have finished.
 *
 * **An arc carries `archived` or `visible` and no other visibility word**, and
 * archiving deliberately does not touch `status:`. All 16 filed today are still
 * `parked`, which is the point: one word per file undoes the sweep, and the
 * status keeps saying what the work was.
 *
 * Any other word shows, matching the law the feed follows for notes — a state
 * this build does not know must opt into hiding rather than fall into it. That
 * matters here because `done` is exactly the word that must NOT mean archived:
 * an arc's lifecycle is its status, and a second field able to say "finished"
 * is the confusion the rule exists to avoid.
 */
export function isArchived(arc: Arc): boolean {
  return arc.archived;
}

/**
 * The one prose line a face carries, and whether it is the arc ASKING rather
 * than describing itself.
 *
 * The ask outranks everything else it could say: an arc waiting on its user is
 * met by scrolling the board, so a question that only appears once the arc is
 * opened is a question that does not get read. Shared by the row and the card
 * because a rule about what an arc leads with must not have two answers.
 *
 * **A done arc carries no line at all.** Evidence is a run of commit hashes on
 * most arcs here, so the face that should have been the smallest on the board
 * was the tallest — a wall of text where a finished thing wants a title and a
 * tick. The evidence is still on the arc's own page, which is where someone
 * asking what it took is already standing.
 *
 * **Next comes the first step**, on the statuses where a way in is wanted. An
 * arc being worked or queued is met on the board by someone deciding what to
 * pick up, and how to begin is the line that helps then; the criterion stays
 * the fallback for an arc that states no first step.
 */
export function faceLine(arc: Arc): { text: string; asking: boolean; starting: boolean } {
  const needs = flat(arc.frontMatter['needs']);
  if (needs && arc.status === 'needs-input') return { text: needs, asking: true, starting: false };
  if (arc.status === 'done') return { text: '', asking: false, starting: false };
  const start = flat(arc.frontMatter['start-with']);
  if (start && SHOWS_FIRST_STEP.includes(arc.status)) return { text: start, asking: false, starting: true };
  return { text: flat(arc.frontMatter['done-when']), asking: false, starting: false };
}

/**
 * How much of a storyline is still open: the arcs it holds that are neither
 * done nor filed away — the phone's "N inside", which stopped counting
 * finished work so a release storyline mostly done does not read as a backlog.
 * Zero for an arc that holds nothing.
 */
export function openInside(arc: Arc, all: readonly Arc[]): number {
  const names = new Set(innerArcNames(arc));
  if (names.size === 0) return 0;
  return all.filter((a) => names.has(a.name) && a.status !== 'done' && !a.archived).length;
}

/**
 * What an arc needs next: its ask while it waits on you — empty where it
 * states none — or its first step while it is in play or queued. Nothing for
 * anything else: a parked or done arc stays quiet.
 */
export function arcNeed(arc: Arc): { text: string; asking: boolean } | null {
  if (arc.status === 'needs-input') return { text: flat(arc.frontMatter['needs']), asking: true };
  const start = flat(arc.frontMatter['start-with']);
  return start && SHOWS_FIRST_STEP.includes(arc.status) ? { text: start, asking: false } : null;
}

/**
 * The one line under an arc on the plan: what it needs, an ask said as one.
 * The done-when is its own page's.
 */
export function planLine(arc: Arc): { text: string; asking: boolean } | null {
  const need = arcNeed(arc);
  if (!need?.asking) return need;
  return { text: need.text ? `Waiting on you — ${need.text}` : 'Waiting on you', asking: true };
}

/**
 * Where an arc offers its first step without being opened. Mirrors
 * `ArcStatus.showsFirstStep` in the app: a `needs-input` arc offers its ask
 * instead, and a parked or done one stays quiet.
 */
export const SHOWS_FIRST_STEP: readonly Status[] = ['active', 'next'];

/**
 * The arcs a storyline holds, from its own `arcs:` key.
 *
 * NOT `sources:`. Several arcs cite another arc there as where they came
 * from, and a citation is not a grouping — reading membership off `sources:`
 * turns every cross-reference into a storyline and hides the cited arc from
 * the board.
 */
export function innerArcNames(arc: Arc): string[] {
  return refNames(arc.frontMatter['arcs']).filter((p) => p !== arc.name);
}

/**
 * The other direction: the storylines holding this arc.
 *
 * Membership is declared on the HOLDER, so the only way to answer "what am I
 * in" is to walk everything and ask each what it holds. That is cheap here
 * because the board already has the whole plan loaded, and it is the reason
 * this belongs beside [innerArcNames] rather than on a page — the two read
 * the same key from opposite ends, and reading it off `sources:` would be
 * wrong for both.
 *
 * An arc can sit in more than one storyline, so this is a list.
 */
export function heldBy(arc: Arc, all: readonly Arc[]): Arc[] {
  return all.filter((other) => other.name !== arc.name && innerArcNames(other).includes(arc.name));
}
